import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Server, Zap, Shield, Globe, ChevronLeft, ChevronRight } from 'lucide-react';
import { getStorageUrl } from '../utils/imageUrlUpdater';

const DedicatedServer: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentGallerySlide, setCurrentGallerySlide] = useState(0);

  // Set page title and canonical URL
  useEffect(() => {
    document.title = 'Thuê Máy Chủ Vật Lý - VMon Cloud';
    
    // Set canonical URL
    const existingCanonical = document.querySelector('link[rel="canonical"]');
    if (existingCanonical) {
      existingCanonical.remove();
    }
    
    const canonical = document.createElement('link');
    canonical.rel = 'canonical';
    canonical.href = 'https://vmon.vn/thue-may-chu';
    document.head.appendChild(canonical);
    
    return () => {
      if (canonical.parentNode) {
        canonical.parentNode.removeChild(canonical);
      }
      document.title = 'VMon Cloud - Dịch vụ VPS và Hosting chất lượng cao';
    };
  }, []);

  const plans = [
    {
      name: 'Intel E3-1230V6',
      specs: '3.50 GHz - 3.90 GHz',
      cores: '4 Cores - 8 Threads',
      ram: '32 GB',
      storage: '512 GB SSD',
      network: '500 Mbps',
      bandwidth: '15 TB',
      price: '1.550.000 VNĐ /Tháng',
      popular: false
    },
    {
      name: 'Intel E-2186G',
      specs: '3.80 GHz - 4.70 GHz',
      cores: '6 Cores - 12 Threads',
      ram: '32 GB',
      storage: '512 GB SSD',
      network: '500 Mbps',
      bandwidth: '15 TB',
      price: '2.250.000 VNĐ /Tháng',
      popular: false
    },
    {
      name: 'Dual Intel E5-2680V4',
      specs: '2.40 GHz - 3.30 GHz',
      cores: '28 Cores - 56 Threads',
      ram: '64 GB',
      storage: '2 x 512 GB SSD',
      network: '1000 Mbps',
      bandwidth: '15 TB',
      price: '2.450.000 VNĐ /Tháng',
      popular: true
    },
    {
      name: 'Dual Intel Gold 6148',
      specs: '2.40 GHz - 3.70 GHz',
      cores: '40 Cores - 80 Threads',
      ram: '64 GB',
      storage: '2 x 512 GB SSD',
      network: '1000 Mbps',
      bandwidth: '15 TB',
      price: '2.950.000 VNĐ /Tháng',
      popular: true
    }
  ];

  const additionalPricing = [
    { item: '1 IP', price: '50.000 VNĐ / Tháng' },
    { item: '32 GB Ram', price: '300.000 VNĐ / Tháng' },
    { item: 'Ổ SSD 512GB', price: '300.000 VNĐ / Tháng' },
    { item: 'Ổ SSD 1TB', price: '600.000 VNĐ / Tháng' },
    { item: 'Ổ SSD NVMe 1TB', price: '700.000 VNĐ / Tháng' }
  ];

  const advantages = [
    {
      icon: Server,
      title: 'PHẦN CỨNG DOANH NGHIỆP',
      description: 'Chúng tôi chỉ sử dụng phần cứng doanh nghiệp của Supermicro, Dell, Intel, Samsung.'
    },
    {
      icon: Shield,
      title: 'KHẢ NĂNG TUỲ CHỈNH',
      description: 'Bạn có thể tuỳ chỉnh cấu hình tuỳ theo nhu cầu sử dụng của mình. Chúng tôi luôn có những phần cứng dự phòng để đáp ứng nhu cầu của bạn.'
    },
    {
      icon: Zap,
      title: 'CÀI ĐẶT NHANH CHÓNG',
      description: 'Quá trình đăng ký và triển khai dịch vụ máy chủ vật lý diễn ra nhanh chóng và thuận tiện.'
    },
    {
      icon: Globe,
      title: 'ĐA DẠNG HỆ ĐIỀU HÀNH',
      description: 'Máy chủ chuyên dụng tại VMon Cloud khả dụng với hệ điều hành Linux hoặc Windows. Ngoài ra còn hỗ trợ CentOS, Ubuntu Server.'
    }
  ];

  const benefits = [
    {
      icon: Server,
      title: 'NHIỀU ĐỊA ĐIỂM',
      description: 'Hiện chúng tôi có mặt ở cả 3 châu lục với dịch vụ thuê máy chủ.'
    },
    {
      icon: Shield,
      title: 'THÔNG TIN RÕ RÀNG, MINH BẠCH',
      description: 'Tất cả các thông tin liên quan đến điều khoản và chính sách của VMon Cloud có nội dung thể hiện đầy đủ, chi tiết và được công khai trên trang chủ giúp cho khách hàng có được sự an tâm tuyệt đối.'
    },
    {
      icon: Zap,
      title: 'TOÀN QUYỀN TRUY CẬP ROOT',
      description: 'Với dịch vụ thuê máy chủ, Bạn sẽ có toàn quyền kiểm soát máy chủ của mình thông qua Kết nối Máy tính Từ xa hoặc SSH.'
    },
    {
      icon: Globe,
      title: 'HỖ TRỢ 24/7/365',
      description: 'Chúng tôi luôn ở đây, bất cứ khi nào bạn cần!'
    }
  ];

  const faqItems = [
    {
      question: 'Dịch vụ thuê máy chủ là gì?',
      answer: 'Dịch vụ cho thuê server hay cho thuê máy chủ là dịch vụ nhà cung cấp cho khách hàng thuê trọn gói cả phần cứng server và không gian đặt server trên hệ thống tủ Rack tiêu chuẩn của trung tâm dữ liệu.'
    },
    {
      question: 'Làm sao để thuê máy chủ giá rẻ mà vẫn đáp ứng được nhu cầu?',
      answer: 'Mỗi khách hàng có những nhu cầu riêng biệt. Để biết được loại máy chủ nào phù hợp với dịch vụ kinh doanh của mình, bạn có thể liên hệ và cung cấp thông tin cho đội ngũ hỗ trợ của VMon Cloud. Chúng tôi sẽ tư vấn cho bạn gói thuê máy chủ giá rẻ hợp lý nhất với chất lượng xứng tầm.'
    },
    {
      question: 'Thuê máy chủ có lợi ích gì?',
      answer: 'Việc đầu tư máy chủ riêng tại doanh nghiệp trên thực tế khá tốn kém và phức tạp. Để sở hữu server hoàn hảo, doanh nghiệp cần phải chi số vốn rất lớn trong quá trình đầu tư và cả quá trình vận hành hệ thống.'
    },
    {
      question: 'Những tình huống có thể gặp khi thuê server là gì?',
      answer: 'Hệ thống bị Downtime: Đây là vấn đề có thể xảy ra nhưng bạn hãy yên tâm vì trường hợp này rất ít khi gặp. Bảo mật dữ liệu: Dữ liệu của bạn sẽ do bạn quản lý nhưng có thể xảy ra vấn đề với ổ cứng. Nếu không chia Raid, hãy nhớ back up dữ liệu của mình thường xuyên. Hãy liên hệ với đội ngũ kỹ thuật của chúng tôi. Chúng tôi luôn ở đây đồng hành cùng bạn.'
    }
  ];

  const galleryImages = [
    {
      src: "https://images.pexels.com/photos/325229/pexels-photo-325229.jpeg",
      title: "Giám Sát máy chủ",
      description: "Theo Dõi Server một cách thuận tiện Từ CPU, RAM, Disk, Network."
    },
    {
      src: "https://images.pexels.com/photos/1181354/pexels-photo-1181354.jpeg",
      title: "Quản lý máy chủ",
      description: "Quản lý các thông tin server đầy đủ và tiện lợi."
    },
    {
      src: "https://images.pexels.com/photos/1181271/pexels-photo-1181271.jpeg",
      title: "Quản Lý Hoá Đơn",
      description: "Quản lý hoá đơn đầy đủ và minh bạch khi thuê máy chủ."
    },
    {
      src: "https://images.pexels.com/photos/1181675/pexels-photo-1181675.jpeg",
      title: "Quản lý máy chủ trên điện thoại",
      description: "Quản lý server trên điện thoại thuận tiện, dễ sử dụng."
    },
    {
      src: "https://images.pexels.com/photos/1181467/pexels-photo-1181467.jpeg",
      title: "Danh Sách Server",
      description: "Xem danh sách máy chủ một cách thuận tiện, đầy đủ thông tin."
    }
  ];

  // Navigation functions for testimonials slider
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % galleryImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  // Navigation functions for gallery slider
  const nextGallerySlide = () => {
    setCurrentGallerySlide((prev) => (prev + 1) % galleryImages.length);
  };

  const prevGallerySlide = () => {
    setCurrentGallerySlide((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Thuê Máy Chủ</h1>
            <p className="text-xl mb-8 max-w-3xl mx-auto">
              Thuê máy chủ vật lý với phần cứng mạnh mẽ - Ổn định tuyệt đối, mức giá cạnh tranh, khả dụng với hệ điều hành Linux hoặc Windows.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">BẢNG GIÁ ƯU ĐÃI KHI THUÊ MÁY CHỦ TẠI VMON CLOUD</h2>
            <p className="text-lg text-gray-600 mb-8">Lựa chọn gói thuê Server mong muốn của bạn. Liên hệ để tư vấn trực tiếp.</p>
          </div>

          {/* Pricing Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-lg overflow-hidden shadow-lg">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CPU</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cores - Thread</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">RAM</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ổ Cứng</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Network</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bandwith</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Giá</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {plans.map((plan, index) => (
                  <tr key={index} className={plan.popular ? "bg-blue-50" : ""}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{plan.name}</div>
                      <div className="text-sm text-gray-500">{plan.specs}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{plan.cores}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{plan.ram}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{plan.storage}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{plan.network}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{plan.bandwidth}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-blue-600">{plan.price}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Additional Pricing */}
          <div className="mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Bảng Giá Đăng Ký Thêm RAM, Ổ Cứng, IP</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {additionalPricing.map((item, index) => (
                <div key={index} className="bg-white p-4 rounded-lg shadow text-center">
                  <div className="flex items-center justify-center mb-2">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                    <span className="font-semibold text-gray-900">{item.item}</span>
                  </div>
                  <p className="text-blue-600 font-medium">{item.price}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-10 text-center">
            <a
              href="https://cloud.vmon.vn/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
            >
              Liên Hệ Chúng Tôi Để Được Tư Vấn Thuê Máy Chủ Tốt Nhất
            </a>
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">NHỮNG ƯU ĐIỂM VƯỢT TRỘI KHI THUÊ MÁY CHỦ VẬT LÝ TẠI VMON CLOUD</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {advantages.map((advantage, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-lg text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <advantage.icon className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{advantage.title}</h3>
                <p className="text-gray-600">{advantage.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">LỢI ÍCH KHI THUÊ MÁY CHỦ TẠI VMON CLOUD</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-6 shadow-lg text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Thư Viện Hình Ảnh</h2>
            <p className="text-lg text-gray-600">Quản lý tài khoản, thuê và giám sát máy chủ dễ dàng với trang panel hiện đại, thân thiện dễ sử dụng.</p>
          </div>

          {/* Gallery Slider */}
          <div className="relative max-w-4xl mx-auto">
            <div className="overflow-hidden rounded-lg shadow-xl">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentGallerySlide * 100}%)` }}
              >
                {galleryImages.map((image, index) => (
                  <div key={index} className="w-full flex-shrink-0 p-4 bg-white">
                    <img
                      src={image.src}
                      alt={image.title}
                      className="w-full h-64 object-cover rounded-lg"
                    />
                    <div className="p-4 text-center">
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">{image.title}</h3>
                      <p className="text-gray-600">{image.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Navigation Buttons */}
            <button
              onClick={prevGallerySlide}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-3 shadow-lg transition-all"
            >
              <ChevronLeft className="h-6 w-6 text-gray-600" />
            </button>
            <button
              onClick={nextGallerySlide}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-3 shadow-lg transition-all"
            >
              <ChevronRight className="h-6 w-6 text-gray-600" />
            </button>
            
            {/* Dots Indicator */}
            <div className="flex justify-center mt-6 space-x-2">
              {galleryImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentGallerySlide(index)}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    index === currentGallerySlide ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related Services Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Các Dịch Vụ Khác Tại VMon Cloud</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-lg shadow-lg overflow-hidden">
              <div className="p-6">
                <div className="flex justify-center mb-4">
                  <img src={getStorageUrl('flag/usa-flag.svg')} alt="USA Flag" className="h-12" />
                </div>
                <h3 className="text-xl font-bold text-center text-gray-900 mb-3">VPS US - Mỹ</h3>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                    <span className="text-gray-700">Không bị giới hạn lượng Data Transfer</span>
                  </li>
                  <li className="flex items-center text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                    <span className="text-gray-700">Bảo mật toàn diện, tuyệt đối</span>
                  </li>
                  <li className="flex items-center text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                    <span className="text-gray-700">Dễ dàng sử dụng</span>
                  </li>
                </ul>
                <div className="text-center">
                  <a
                    href="https://cloud.vmon.vn/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Xem chi tiết
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg shadow-lg overflow-hidden">
              <div className="p-6">
                <div className="flex justify-center mb-4">
                  <img src={getStorageUrl('flag/eu-flag.svg')} alt="EU Flag" className="h-12" />
                </div>
                <h3 className="text-xl font-bold text-center text-gray-900 mb-3">VPS EU - Châu Âu</h3>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                    <span className="text-gray-700">Backup tự động 1 tuần/lần</span>
                  </li>
                  <li className="flex items-center text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                    <span className="text-gray-700">Tối ưu hóa cho xử lý hiệu năng cao</span>
                  </li>
                  <li className="flex items-center text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                    <span className="text-gray-700">Truy cập dữ liệu siêu nhanh</span>
                  </li>
                </ul>
                <div className="text-center">
                  <a
                    href="https://cloud.vmon.vn/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Xem chi tiết
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg shadow-lg overflow-hidden">
              <div className="p-6">
                <div className="flex justify-center mb-4">
                  <img src={getStorageUrl('flag/singapore-flag.svg')} alt="Singapore Flag" className="h-12" />
                </div>
                <h3 className="text-xl font-bold text-center text-gray-900 mb-3">VPS Singapore</h3>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                    <span className="text-gray-700">Hỗ trợ tận tâm 24/7</span>
                  </li>
                  <li className="flex items-center text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                    <span className="text-gray-700">Trình quản lý vô cùng thân thiện</span>
                  </li>
                  <li className="flex items-center text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                    <span className="text-gray-700">Dễ dàng nâng cấp</span>
                  </li>
                </ul>
                <div className="text-center">
                  <a
                    href="https://cloud.vmon.vn/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Xem chi tiết
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">CÁC CÂU HỎI THƯỜNG GẶP KHI THUÊ SERVER?</h2>
          </div>

          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <details key={index} className="bg-white rounded-lg p-6 shadow-lg group">
                <summary className="font-semibold text-gray-900 cursor-pointer list-none flex justify-between items-center">
                  {item.question}
                  <span className="transition-transform group-open:rotate-180">
                    <ChevronRight className="h-5 w-5 text-gray-500" />
                  </span>
                </summary>
                <div className="mt-4 text-gray-600">{item.answer}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Cùng nhau, Trực tuyến.
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Chúng tôi ở đây giúp các bạn có thể làm việc online một cách hiệu quả nhất.
          </p>
          <a
            href="https://cloud.vmon.vn/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-blue-600 bg-white hover:bg-blue-50"
          >
            Liên hệ ngay
          </a>
        </div>
      </section>
    </div>
  );
};

export default DedicatedServer;